import { useRef } from "react";
import { useSelector } from "react-redux";
import { Roles } from "@evennt-tech/api-v2-core";
import { selectUser } from "store/auth/selectors";
import useViewMode, { ViewMode } from "store/viewMode/viewMode.store";
import useStableTranslate from "hooks/useStableTranslate";
import { LocalizedNameTranslate } from "tools/localization/resolveLocalizedName";

type TranslateFn = NonNullable<LocalizedNameTranslate>;

export const useLocalizedNameTranslate = (): LocalizedNameTranslate => {
  const { t, language } = useStableTranslate();
  const { role } = useSelector(selectUser);
  const mode = useViewMode((state) => state.mode);

  const isExhibitor = role === Roles.EXHIBITOR || mode === ViewMode.EXHIBITOR;
  const identityKey = `${isExhibitor}|${language}`;

  // A new identity per language lets consumers keep `t` as a memo dependency
  // and still recompute on a language switch, without recomputing on every
  // other Tolgee update.
  const cache = useRef<{ key: string; fn: LocalizedNameTranslate }>();
  let cached = cache.current;
  if (!cached || cached.key !== identityKey) {
    cached = {
      key: identityKey,
      fn: isExhibitor
        ? (((...args: Parameters<TranslateFn>) => t(...args)) as TranslateFn)
        : null,
    };
    cache.current = cached;
  }

  return cached.fn;
};
